import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Code2, Film, Layers, Scissors, Pause, SkipBack, SkipForward, Maximize2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, RefreshCcw } from "lucide-react";

// Isometric workstation scene for the Meraki Soul section
// Seperated from Hero station for independent control and styling
// Updated to match the aesthetic of the provided reference screenshot
const MerakiSoulStation = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 10, ry: -8 });
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play().catch(() => { });
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  const handleManualTilt = (dir) => {
    setTilt(prev => {
      let next = { ...prev };
      if (dir === "up") next.rx = prev.rx + 20;
      if (dir === "down") next.rx = prev.rx - 20;
      if (dir === "left") next.ry = prev.ry - 20;
      if (dir === "right") next.ry = prev.ry + 20;
      if (dir === "reset") return { rx: 10, ry: -8 };
      return next;
    });
  };

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[1/1] sm:aspect-[5/4]"
      style={{ perspective: "1600px" }}
    >
      <motion.div
        animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
        transition={{ type: "spring", stiffness: 70, damping: 16 }}
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Floor grid - matching screenshot density */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: "8%",
            width: "120%",
            height: "60%",
            transform: "translate3d(-50%, 0, 0) rotateX(75deg)",
            transformOrigin: "center bottom",
            backgroundImage:
              "linear-gradient(rgba(201,169,97,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.06) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)"
          }}
        />

        {/* Ambient top glow */}
        <div
          className="absolute left-1/2 top-0 w-[400px] h-[300px] -translate-x-1/2 opacity-60"
          style={{
            transform: "translate3d(-50%, -60px, -100px)",
            background: "radial-gradient(ellipse at top, rgba(228,199,122,0.3), transparent 70%)",
            filter: "blur(20px)"
          }}
        />

        {/* MONITOR ASSEMBLY - Elevated for better visibility */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-[88%] sm:w-[72%] h-[60%] sm:h-[54%]"
          style={{
            bottom: "26%",
            transform: "translate3d(-50%, 0, 20px)",
            zIndex: 10
          }}
        >
          {/* Main frame matching screenshot */}
          <div
            className="w-full h-full rounded-sm overflow-hidden relative group bg-[#0a0908] border border-[rgba(201,169,97,0.35)] shadow-[0_40px_100px_rgba(0,0,0,0.8)]"
          >
            {/* Top window bar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-[rgba(201,169,97,0.15)] bg-black/60">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#e4c77a]/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/5" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/5" />
              </div>
              <div className="font-impact text-[9px] tracking-[0.4em] text-[var(--muted)] uppercase">AURORA_V12.FINAL.MERAKI.MP4</div>
              <Maximize2 size={10} color="#8a7e6d" />
            </div>

            {/* Layout grid */}
            <div className="grid grid-cols-[56px_1fr] h-[calc(100%-82px)]">
              {/* Sidebar tools */}
              <div className="flex flex-col items-center gap-4 py-5 border-r border-[rgba(201,169,97,0.12)] bg-black/30">
                {[Scissors, Film, Layers, Code2].map((Ic, i) => (
                  <div key={i} className={`w-9 h-9 rounded-md flex items-center justify-center transition-all ${i === 0 ? "bg-[var(--gold)]/15 border border-[var(--gold)]/50" : "hover:bg-white/5"}`}>
                    <Ic size={14} color={i === 0 ? "#e4c77a" : "#8a7e6d"} strokeWidth={1.5} />
                  </div>
                ))}
              </div>

              {/* Video Interface */}
              <div className="relative w-full h-full p-2 bg-[#121110]">
                <div className="w-full h-full relative rounded-[2px] overflow-hidden border border-white/5">
                  {playing ? (
                    <iframe
                      className="absolute inset-0 w-full h-full border-0"
                      src="https://www.youtube.com/embed/KAnKujVDjf4?si=LOKq4Q3JPD7oBjCK&start=535&autoplay=1&controls=0"
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        className="absolute inset-0 w-full h-full object-cover opacity-50 transition-opacity duration-700"
                        loop
                        muted
                        playsInline
                        poster="https://img.youtube.com/vi/KAnKujVDjf4/maxresdefault.jpg"
                      >
                        <source src="/videos/workstation-preview.mp4" type="video/mp4" />
                      </video>

                      {/* Large Centered Play Button like screenshot */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <motion.div 
                          animate={{ scale: [1, 1.05, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-16 h-16 rounded-full flex items-center justify-center border-2 border-[#e4c77a] bg-black/30 backdrop-blur-sm shadow-[0_0_30px_rgba(228,199,122,0.2)]"
                        >
                          <Play size={20} color="#e4c77a" fill="#e4c77a" className="ml-1" />
                        </motion.div>
                      </div>
                    </>
                  )}
                  
                  {/* Timecode overlay in video */}
                  <div className="absolute top-4 left-4 font-impact text-[10px] tracking-[0.2em] text-[#e4c77a] bg-black/70 border border-[#e4c77a]/30 px-3 py-1 rounded-sm pointer-events-none z-10">
                    00:01:42:18
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Timeline matching screenshot */}
            <div className="absolute bottom-0 left-0 right-0 h-[42px] bg-[#0a0908] border-t border-[rgba(201,169,97,0.2)] px-4 py-2">
              <div className="flex items-center gap-3 h-full">
                <div className="flex items-center gap-2 pr-2 border-r border-white/5">
                  <SkipBack size={10} color="#8a7e6d" />
                  <Pause size={10} color="#e4c77a" fill="#e4c77a" />
                  <SkipForward size={10} color="#8a7e6d" />
                </div>
                <div className="flex-1 flex flex-col justify-center gap-[3px]">
                   {/* Track 1 */}
                   <div className="h-1 flex gap-1">
                      <div className="flex-[4] bg-[var(--gold)]/20 rounded-full" />
                      <div className="flex-[8] bg-[var(--gold)]/60 rounded-full" />
                      <div className="flex-[3] bg-[var(--gold)]/10 rounded-full" />
                   </div>
                   {/* Track 2 */}
                   <div className="h-1 flex gap-1">
                      <div className="flex-[6] bg-[#b87333]/40 rounded-full" />
                      <div className="flex-[2] bg-white/5 rounded-full" />
                      <div className="flex-[9] bg-[#b87333]/20 rounded-full" />
                   </div>
                   {/* Track 3 */}
                   <div className="h-1 flex gap-1 opacity-40">
                      <div className="flex-[10] bg-white/5 rounded-full" />
                      <div className="flex-[4] bg-[var(--gold)]/30 rounded-full" />
                   </div>
                </div>
                {/* Vertical Playhead */}
                <div className="absolute top-0 bottom-0 w-px bg-[var(--gold)] shadow-[0_0_8px_var(--gold)]" style={{ left: "44%" }} />
              </div>
            </div>

            {/* INTERACTION SHIELD */}
            <div 
              className="absolute inset-0 z-[100] cursor-hover"
              style={{ transform: "translate3d(0,0,160px)", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); setPlaying(!playing); }}
              data-cursor={playing ? "Pause" : "Play"}
            />
          </div>
        </div>

        {/* KEYBOARD - elevated and more visible */}
        <div
          className="absolute left-1/2 pointer-events-none w-[70%] sm:w-[56%] h-[16%] sm:h-[14%]"
          style={{
            bottom: "10%",
            transform: "translate3d(-50%, 0, 80px) rotateX(65deg)",
            transformOrigin: "center bottom",
            borderRadius: 4,
            background: "linear-gradient(180deg, #1f1a16, #0a0908)",
            border: "1px solid rgba(228,199,122,0.3)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.5)"
          }}
        >
          <div className="grid grid-cols-12 gap-[3px] p-3 h-full" style={{ gridTemplateColumns: "repeat(12, 1fr)" }}>
            {Array.from({ length: 36 }).map((_, i) => (
              <KeyboardKey key={i} delay={Math.random() * 5} />
            ))}
          </div>
        </div>

        {/* COFFEE MUG */}
        <div className="absolute" style={{ bottom: "16%", right: "8%", transform: "translateZ(100px)" }}>
           <div className="w-12 h-14 bg-gradient-to-b from-[#1a1613] to-[#0a0908] border border-[rgba(201,169,97,0.25)] rounded-b-sm relative">
              <div className="absolute -right-2 top-2 w-3 h-6 rounded-r-full border border-[rgba(201,169,97,0.25)] border-l-0" />
           </div>
        </div>

        {/* Floating Tags - exact screenshot style */}
        <FloatTag text="<Film />" left="4%" top="18%" z={140} delay={0} />
        <FloatTag text="{ craft: true }" right="2%" top="12%" z={120} delay={0.6} />
        <FloatTag text="// cut_at(1:42)" left="10%" top="60%" z={160} delay={1.2} />
        <FloatTag text="<Story />" right="6%" top="58%" z={130} delay={1.8} />
      </motion.div>

      {/* Manual Controls */}
      <div className="absolute bottom-4 right-4 z-50 flex flex-col items-center gap-1 bg-black/20 p-2 rounded-xl backdrop-blur-sm border border-white/5">
        <button onClick={() => handleManualTilt("up")} className="w-8 h-8 rounded-lg flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] text-[var(--cream-muted)]" data-cursor="Click"><ChevronUp size={16} /></button>
        <div className="flex gap-1">
          <button onClick={() => handleManualTilt("left")} className="w-8 h-8 rounded-lg flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] text-[var(--cream-muted)]" data-cursor="Click"><ChevronLeft size={16} /></button>
          <button onClick={() => handleManualTilt("reset")} className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--gold)]/10 border border-[var(--gold)]/30 text-[var(--gold)]" data-cursor="Reset"><RefreshCcw size={14} /></button>
          <button onClick={() => handleManualTilt("right")} className="w-8 h-8 rounded-lg flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] text-[var(--cream-muted)]" data-cursor="Click"><ChevronRight size={16} /></button>
        </div>
        <button onClick={() => handleManualTilt("down")} className="w-8 h-8 rounded-lg flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] text-[var(--cream-muted)]" data-cursor="Click"><ChevronDown size={16} /></button>
      </div>
    </div>
  );
};

const KeyboardKey = ({ delay }) => (
  <motion.div
    animate={{ 
      opacity: [0.3, 0.8, 0.3],
      scale: [1, 0.94, 1],
      backgroundColor: ["rgba(228,199,122,0.1)", "rgba(228,199,122,0.4)", "rgba(228,199,122,0.1)"]
    }}
    transition={{ 
      duration: 0.2 + Math.random() * 0.5,
      delay: delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 8
    }}
    className="rounded-[1px] border border-[var(--gold)]/30 w-full h-full"
  />
);

const FloatTag = ({ text, left, right, top, z = 50, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
    transition={{ opacity: { duration: 0.8, delay }, scale: { duration: 0.8, delay }, y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay } }}
    className="absolute px-3 py-1 sm:px-4 sm:py-1.5 rounded-full font-mono text-[9px] sm:text-[11px] tracking-tight pointer-events-none"
    style={{
      left, right, top,
      transform: `translateZ(${z}px)`,
      background: "rgba(20,17,15,0.85)",
      border: "1px solid rgba(228,199,122,0.45)",
      color: "#e4c77a",
      boxShadow: "0 0 15px rgba(228,199,122,0.1)",
      backdropFilter: "blur(6px)"
    }}
  >
    {text}
  </motion.div>
);

export default MerakiSoulStation;
